export type ComponentDescription = {
	whatItIs: string;
	commonTypes: string[];
	goodFor?: string[];
};

export const COMPONENT_DESCRIPTIONS: Record<string, ComponentDescription> = {
	pool: {
		whatItIs: 'A dedicated swimming area that becomes a central feature for recreation and cooling off.',
		commonTypes: ['In-ground', 'Above-ground', 'Inflatable', 'Infinity edge', 'Natural pool'],
		goodFor: ['Family recreation', 'Hosting', 'Warm climates']
	},
	fence: {
		whatItIs: 'A boundary structure that adds privacy, safety, and visual framing to your garden.',
		commonTypes: ['Wood slat', 'Metal', 'Vinyl', 'Living hedge'],
		goodFor: ['Privacy', 'Pet/kid safety', 'Defining property edges']
	},
	bbq: {
		whatItIs: 'A cooking zone focused on grilling, often paired with prep space and nearby seating.',
		commonTypes: ['Built-in grill', 'Portable grill', 'Gas grill', 'Charcoal grill'],
		goodFor: ['Weekend cooking', 'Entertaining', 'Outdoor dining']
	},
	lounge: {
		whatItIs: 'A comfort-focused seating area designed for relaxing, socializing, and spending time outdoors.',
		commonTypes: ['Outdoor sofa set', 'Dining area', 'Lounge chairs', 'Mixed seating'],
		goodFor: ['Relaxing', 'Hosting friends', 'Reading or quiet time']
	},
	'outdoor-kitchen': {
		whatItIs: 'A more complete outdoor cooking setup that can include grill, sink, storage, and prep counters.',
		commonTypes: ['Grill + counter', 'Grill + sink', 'Kitchen wall', 'Full island kitchen'],
		goodFor: ['Frequent hosting', 'Serious cooking', 'Long outdoor gatherings']
	},
	'fire-pit': {
		whatItIs: 'A focal point for warmth and ambiance that extends garden use into evenings and cooler months.',
		commonTypes: ['Wood-burning pit', 'Gas fire pit', 'Sunken fire lounge', 'Outdoor fireplace'],
		goodFor: ['Evening use', 'Atmosphere', 'Gathering circles']
	},
	pergola: {
		whatItIs: 'An open or partially covered structure that creates shade and a defined outdoor room.',
		commonTypes: ['Open pergola', 'Louvered pergola', 'Gazebo', 'Pavilion', 'Pergola with canopy'],
		goodFor: ['Shade', 'Dining zones', 'Visual structure']
	},
	deck: {
		whatItIs: 'A raised or ground-level surface area for seating, dining, and smooth circulation.',
		commonTypes: ['Timber deck', 'Composite deck', 'Paved patio', 'Multi-level deck'],
		goodFor: ['Outdoor living', 'Entertaining', 'Connecting house to garden']
	},
	shed: {
		whatItIs: 'A practical storage structure for tools, equipment, and seasonal items.',
		commonTypes: ['Tool shed', 'Workshop shed', 'Bike storage', 'Garden cabin'],
		goodFor: ['Organization', 'Reducing clutter', 'Protecting equipment']
	},
	playground: {
		whatItIs: 'A dedicated activity area for children with safe surfaces and age-appropriate equipment.',
		commonTypes: ['Swings and slide', 'Climbing frame', 'Sandbox zone', 'Multi-play set'],
		goodFor: ['Kids', 'Family gardens', 'Active outdoor time']
	},
	'vegetable-garden': {
		whatItIs: 'A food-growing zone for vegetables, herbs, and edible plants.',
		commonTypes: ['Raised beds', 'In-ground rows', 'Mixed edible border', 'Container kitchen garden'],
		goodFor: ['Growing food', 'Sustainability', 'Hands-on gardening']
	},
	'water-feature': {
		whatItIs: 'A decorative or naturalistic water element that adds movement, sound, and calm.',
		commonTypes: ['Pond', 'Waterfall', 'Stream', 'Fountain'],
		goodFor: ['Relaxing ambiance', 'Wildlife support', 'Visual focal point']
	},
	lighting: {
		whatItIs: 'Functional and decorative lighting that improves safety and extends evening usability.',
		commonTypes: ['Path lights', 'Spotlights', 'String lights', 'Wall and step lights'],
		goodFor: ['Night use', 'Atmosphere', 'Highlighting key features']
	},
	pathway: {
		whatItIs: 'A circulation route that guides movement and connects different zones in the garden.',
		commonTypes: ['Gravel path', 'Stepping stones', 'Pavers', 'Concrete walkway'],
		goodFor: ['Accessibility', 'Structure and flow', 'Cleaner movement']
	},
	'retaining-wall': {
		whatItIs: 'A structural wall that holds soil on sloped ground and creates level usable areas.',
		commonTypes: ['Block wall', 'Natural stone wall', 'Gabion wall', 'Timber sleeper wall'],
		goodFor: ['Sloped sites', 'Erosion control', 'Creating terraces']
	},
	driveway: {
		whatItIs: 'A durable surface for vehicle access and parking, often integrated with the front garden.',
		commonTypes: ['Pavers', 'Concrete', 'Gravel', 'Resin-bound'],
		goodFor: ['Parking', 'Access', 'Front-of-home curb appeal']
	},
	greenhouse: {
		whatItIs: 'A protected growing structure that supports seedlings and longer growing seasons.',
		commonTypes: ['Glass greenhouse', 'Polycarbonate greenhouse', 'Cold frame', 'Mini greenhouse'],
		goodFor: ['Plant propagation', 'Year-round growing', 'Food production']
	},
	'flower-beds': {
		whatItIs: 'Planted ornamental areas that add color, texture, and seasonal change.',
		commonTypes: ['Border beds', 'Island beds', 'Formal beds', 'Cottage-style mixed beds'],
		goodFor: ['Visual impact', 'Pollinator support', 'Softening hardscapes']
	},
	'hot-tub': {
		whatItIs: 'A heated spa feature for relaxation and wellness, often paired with privacy or decking.',
		commonTypes: ['Portable hot tub', 'In-ground spa', 'Inflatable hot tub', 'Deck-integrated spa', 'Covered spa nook'],
		goodFor: ['Relaxation', 'Wellness', 'Year-round enjoyment']
	},
	'pizza-oven': {
		whatItIs: 'A high-heat outdoor cooking feature that supports pizza and other wood- or gas-fired dishes.',
		commonTypes: ['Wood-fired masonry', 'Portable oven', 'Gas-assisted oven', 'Built-in oven station'],
		goodFor: ['Hosting', 'Specialty cooking', 'Outdoor kitchen upgrades']
	},
	compost: {
		whatItIs: 'A dedicated area to recycle green waste into nutrient-rich compost for planting.',
		commonTypes: ['Single bin', 'Multi-bay system', 'Tumbler', 'Screened compost corner'],
		goodFor: ['Sustainability', 'Soil health', 'Reducing waste']
	}
};
